import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseLabel from "@reusables/BaseLabel";
import { productsApi, useGetModuleProductQuery } from "@redux/features/products/productsApi";
import { Controller, DefaultValues, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseDropdown, { adaptInfiniteLoader } from "@reusables/dropdowns/BaseDropdown";
import BaseInput from "@reusables/BaseInput";
import BaseDatepicker from "@reusables/BaseDatepicker";
import BaseButton from "@reusables/BaseButton";
import BaseInfiniteLoader from "@reusables/dropdowns/BaseInfiniteLoader";
import CategoryCommandDropdown from "@ready/CategoryCommandDropdown";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { toastError } from "@helpers/utils";
import { useAppDispatch } from "@redux/hooks";
import { Product as ProductType } from "@/types/general";
import { CategoryItem, customerGroupItemScheme, DiscountItem, ProductItem } from "../../types";

export interface MutationProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onItemAddition: (item: DiscountItem) => void;
    item?: DiscountItem;
}

const emptyProductItem: DefaultValues<ProductItem> = {
    type: "product",
    product: undefined,
    sale_price: undefined,
    valid_from: null,
    valid_to: null,
};

const emptyCategoryItem: DefaultValues<CategoryItem> = {
    type: "category",
    category: undefined,
    valid_from: null,
    valid_to: null,
};

export default function MutationProductModal({
    isOpen,
    onClose,
    onItemAddition,
    item,
}: MutationProductModalProps): JSX.Element {
    const { t } = useTranslation("", { keyPrefix: "customerGroups.mutation.modals.product" });
    const dispatch = useAppDispatch();

    const productDraft = useRef<DefaultValues<ProductItem>>(emptyProductItem);
    const categoryDraft = useRef<DefaultValues<CategoryItem>>(emptyCategoryItem);
    const [productsOptions, setProductsOptions] = useState<ProductType.Root[]>([]);
    const [isProductOptionsLoading, setIsProductOptionsLoading] = useState(false);

    const form = useForm<DiscountItem>({
        resolver: zodResolver(customerGroupItemScheme),
        defaultValues: emptyProductItem,
    });
    const { getValues, reset, setValue } = form;

    const type = useWatch({ control: form.control, name: "type" });
    const product = useWatch({ control: form.control, name: "product" });

    const { data: selectedProduct } = useGetModuleProductQuery(
        { module: "customer", id: product?.id as number },
        { skip: !product?.id || type !== "product" }
    );

    useEffect(() => {
        if (!isOpen) return;

        productDraft.current = emptyProductItem;
        categoryDraft.current = emptyCategoryItem;
        reset(item ?? emptyProductItem);
    }, [isOpen, item, reset]);

    useEffect(() => {
        if (!selectedProduct || type !== "product") return;

        const isEditingSameProduct =
            item?.type === "product" && item.product.id === selectedProduct.id;

        if (!isEditingSameProduct) {
            setValue("sale_price", selectedProduct.prices.selling_price);
        }
    }, [item, selectedProduct, setValue, type]);

    const handleTypeChange = (nextType: DiscountItem["type"]) => {
        if (item || nextType === type) return;

        const currentItem = getValues();
        if (currentItem.type === "product") {
            productDraft.current = currentItem;
        } else {
            categoryDraft.current = currentItem;
        }

        reset(nextType === "product" ? productDraft.current : categoryDraft.current);
    };

    const handleClose = () => {
        reset(emptyProductItem);
        onClose();
    };

    const onSubmit = form.handleSubmit((data) => {
        onItemAddition(data);
    });

    return (
        <BaseModal isOpen={isOpen} onClose={handleClose} width={900} padding="56px" useCloseIcon>
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="modal-title">
                    {item
                        ? item.type === "product"
                            ? t("headings.editProduct")
                            : t("headings.editCategory")
                        : type === "category"
                        ? t("headings.addCategory")
                        : t("headings.addProduct")}
                </div>

                <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                        <RadioGroup
                            row
                            aria-label={t("type.label") as string}
                            value={field.value ?? "product"}
                            onChange={(_, value) => handleTypeChange(value as DiscountItem["type"])}
                            className="gap-[32px]"
                        >
                            {(["product", "category"] as const).map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    disabled={!!item && item.type !== option}
                                    control={<StyledTypeRadio />}
                                    label={t(`type.${option}`) as string}
                                    sx={{
                                        margin: 0,
                                        opacity: item && item.type !== option ? 0.5 : 1,
                                        ".MuiFormControlLabel-label": {
                                            marginLeft: 1,
                                            color: "#3C3769 !important",
                                            fontWeight: 300,
                                        },
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    )}
                />

                <BaseInputsGrid cols={2}>
                    {type === "product" && (
                        <>
                            <Controller
                                name="product"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <BaseInfiniteLoader
                                        fetch={(search, page, limit) => {
                                            setIsProductOptionsLoading(true);
                                            dispatch(
                                                productsApi.endpoints.getModuleProductsFull.initiate(
                                                    {
                                                        filters: { search },
                                                        pagination: { page, limit },
                                                        module: "customer",
                                                    },
                                                    { subscribe: false }
                                                )
                                            )
                                                .unwrap()
                                                .then((response) => {
                                                    setProductsOptions(response.payload ?? []);
                                                })
                                                .catch((error) => {
                                                    toastError(error);
                                                    console.error(
                                                        "An error occurred in the customer group product loader",
                                                        error
                                                    );
                                                })
                                                .finally(() => {
                                                    setIsProductOptionsLoading(false);
                                                });
                                        }}
                                        limit={100}
                                        result={productsOptions}
                                        isLoading={isProductOptionsLoading}
                                    >
                                        {(infinity) => (
                                            <BaseDropdown
                                                {...field}
                                                {...fieldState}
                                                {...adaptInfiniteLoader(infinity)}
                                                label={t("dropdowns.product.label") + " *"}
                                                placeholder={t("dropdowns.product.placeholder")}
                                                getter={{
                                                    label: (option) => option.name,
                                                    key: (option) => option.id,
                                                    caption: (option) => option.code,
                                                }}
                                                virtualize
                                                autocomplete
                                            />
                                        )}
                                    </BaseInfiniteLoader>
                                )}
                            />

                            <Controller
                                name="sale_price"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}
                                        label={t("fields.unitPrice.label") + " *"}
                                        placeholder={t("fields.unitPrice.placeholder")}
                                        type="number"
                                    />
                                )}
                            />
                        </>
                    )}

                    {type === "category" && (
                        <div className="col-span-2">
                            <Controller
                                name="category"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <div className="flex flex-col">
                                        <BaseLabel>
                                            {t("dropdowns.category.label") + " *"}
                                        </BaseLabel>
                                        <CategoryCommandDropdown
                                            value={field.value?.id}
                                            onSelect={(event) =>
                                                field.onChange(event.current?.item)
                                            }
                                            multiple={false}
                                            module="product"
                                            error={fieldState.error}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    )}

                    <Controller
                        control={form.control}
                        name="valid_from"
                        render={({ field, fieldState }) => (
                            <BaseDatepicker
                                {...field}
                                {...fieldState}
                                label={t("fields.validFrom.label")}
                                placeholder={t("fields.validFrom.placeholder")}
                                disablePast
                            />
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="valid_to"
                        render={({ field, fieldState }) => (
                            <div className="flex flex-column">
                                <BaseLabel>
                                    {t("fields.validTill.label")}{" "}
                                    <span className="font-thin opacity-50">
                                        {t("fields.validTill.tip")}
                                    </span>
                                </BaseLabel>
                                <BaseDatepicker
                                    {...field}
                                    error={fieldState.error}
                                    placeholder={t("fields.validTill.placeholder")}
                                    disablePast
                                />
                            </div>
                        )}
                    />
                </BaseInputsGrid>

                <BaseButton
                    text={item ? t("buttons.save") : t("buttons.add")}
                    size="md"
                    type="submit"
                    className="w-full"
                />
            </form>
        </BaseModal>
    );
}

function StyledTypeRadio(props: React.ComponentProps<typeof Radio>) {
    return (
        <Radio
            {...props}
            icon={<span className="block h-[16px] w-[16px] rounded-[8px]" />}
            checkedIcon={<span className="block h-[16px] w-[16px] rounded-[8px] bg-[#A6DD4C]" />}
            sx={{
                background: "rgba(166, 221, 76, 0.25) !important",
                padding: "6px",
                borderRadius: "8px",
                marginLeft: "10px",
                color: "transparent",
                "&:hover span": {
                    background: "#A6DD4C",
                },
            }}
        />
    );
}
